import { Navigate } from 'react-router-dom';
import { useGroups } from '../contexts/GroupsContext';
import { PageSpinner } from '../components/ui';
import { NoGroupsScreen } from '../screens/home-search/NoGroupsScreen';
import { route } from './paths';

/** Mirrors `useWorkspaceNav`'s own recall key, so `/groups` lands where the sidebar would. */
const LAST_WORKSPACE_KEY = 'cohaven_last_workspace';

function recalledGroupId(ids: string[]): string | null {
    let remembered: string | null = null;
    try {
        remembered = sessionStorage.getItem(LAST_WORKSPACE_KEY);
    } catch {
        remembered = null;
    }

    return ids.find((id) => id === remembered) ?? ids[0] ?? null;
}

/**
 * `/groups` resolves to a group rather than listing them.
 *
 * The grid that used to live here answered "which group?" with a page; the
 * switcher in the hero, the breadcrumb and the mobile bar answers it with a
 * menu, from inside whichever group you are already in. So the path stays — five
 * call sites navigate to it, including `ProductSwitcher`'s fallback and the
 * redirect after a group is deleted — and becomes a resolver, the same shape
 * `HomeRouter` has always had for `/`.
 */
export function GroupsRouter() {
    const { groups, loading } = useGroups();

    // Deciding before the fetch settles would send a member who has groups to
    // onboarding — the mistake `HomeRouter` documents at the top of its own body.
    if (loading) {
        return <PageSpinner />;
    }

    const groupId = recalledGroupId(groups.map((group) => group.id));

    if (groupId) {
        return <Navigate to={route('group', { groupId })} replace />;
    }

    return <NoGroupsScreen />;
}
